<template>
  <TumorBoardUpcomingSession v-if="isPendingSession" />
  <template v-else>
    <div v-if="showNotesComponent" class="new-session-div">
      <div class="backicon-notes-div">
        <span class="material-symbols-outlined hover-effect" @click="showTBNewSessionFunction()">chevron_left</span>

        <span class="label-span">Notes</span>
      </div>
      <div class="custom-textarea-div">
        <Textarea v-model="notesValue" placeholder="Add Notes..." class="custom-textarea"
          @keydown.tab="showTBNewSessionFunction" />
      </div>
    </div>
    <template v-else>
      <div class="new-session-div">
        <div>
          <span class="label-span">Tumor Board</span>
        </div>
        <div class="new-session-dropdown-div">
          <Dropdown v-model="selectedTumorBoard" :options="tumorBoardsDropdown" optionLabel="tbName"
            placeholder="Select Tumor Board" class="custom-placeholder-color" />
        </div>
        <div>
          <span class="label-span">Primary Diagnosis</span>
        </div>
        <div class="new-session-dropdown-div">
          <Dropdown v-model="selectedDiagnosis" :options="patientDiagnosisDropdown" optionLabel="description"
            placeholder="Diagnosis Not Assigned" class="custom-placeholder-color" />
        </div>
        <div>
          <span class="label-span">Priority</span>
        </div>
        <div class="new-session-dropdown-div">
          <Dropdown v-model="selectedPriority" :options="priorityOptions" optionLabel="priority" placeholder="Priority"
            class="current-session-multiselect" />
        </div>
        <div>
          <span class="label-span">Notes</span>
        </div>
        <div class="new-session-notes-button-div">
          <Button :label="dynamicButtonLabel" outlined @click="showNotesComponentFunction()" size="small"
            class="custom-placeholder-color" />
        </div>
        <div class="add-totbqueue-button-div">
          <button @click="addToTBQueue()" :disabled="isAddToTBQueueButtonDisabled" class="primary-filled-button">
            Request to Schedule Patient
          </button>
        </div>
      </div>
    </template>
  </template>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import { getTumorBoards } from './getTumorBoards'
import { getPatientDiagnosis } from './getPatientDiagnosis'
import { updatePatientSchedule } from '@/ux3/cards/TumorBoardSessions/updatePatientSchedule'
import TumorBoardUpcomingSession from './TumorBoardUpcomingSession.vue'
import { addDefaultDiagnosis, showToast } from '@/utils/utils.js'
import { priorityOptions } from '@/common/shared.js'

const store = useStore()
const selectedTumorBoard = ref([])
const selectedDiagnosis = ref({})
const selectedPriority = ref('')
const notesValue = ref('')
const tumorBoardsDropdown = ref([])
const patientDiagnosisDropdown = ref([])
const isPendingSession = ref(false)
const showNotesComponent = ref(false)
const dynamicButtonLabel = ref('Click to Add Notes')
const patient = computed(() => store.getters.session.patient)
const userId = computed(() => store.getters.userId)
const isAddToTBQueueButtonDisabled = computed(() => {
  return (!selectedTumorBoard.value || !selectedTumorBoard.value.tbId) || !selectedPriority.value.priority
})

watch(patient, async (newPatient) => {
  if (!newPatient) { return }
  const tumorBoardsData = await getTumorBoards()
  selectedTumorBoard.value = []
  tumorBoardsDropdown.value = tumorBoardsData?.tb_types?.map(item => ({ tbName: item.tb_name, tbId: item.tb_id })) || []
  const patientDiagnosisData = await getPatientDiagnosis([patient.value.id])
  selectedDiagnosis.value = { description: 'Diagnosis Not Assigned' }
  patientDiagnosisDropdown.value = addDefaultDiagnosis(patientDiagnosisData)
  selectedPriority.value = ''
  notesValue.value = ''
  dynamicButtonLabel.value = 'Click to Add Notes'
}, { immediate: true })

const showNotesComponentFunction = () => {
  showNotesComponent.value = true
}

const showTBNewSessionFunction = () => {
  showNotesComponent.value = false
  dynamicButtonLabel.value = notesValue.value || 'Click to Add Notes'
}

const addToTBQueue = async () => {
  const patientDetails = {
    date: null,
    tb_id: selectedTumorBoard.value.tbId,
    schedule: {
      care_giver_id: userId.value,
      mrn: patient.value.id,
      notes: notesValue.value,
      priority: selectedPriority.value.priority,
      diagnosis: selectedDiagnosis.value.diagnosis,
    },
  }
  const response = await updatePatientSchedule(patientDetails)
  if (response.status) {
    if (response.message.includes('MRN is already Scheduled')) {
      showToast('error', 'Patient is already scheduled for an upcoming tumor board session.')
    } else if (response.status === 401) {
      showToast('error', 'You are not authorized to schedule patients.')
    } else {
      showToast('error', 'There was an issue scheduling the patient.')
    }
    return
  }

  showToast('success', 'Successfully submitted request to schedule patient for an upcoming session.')
  isPendingSession.value = true
}
</script>

<style scoped>
.label-span {
  color: var(--Grey600);
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 18px;
}

.backicon-notes-div {
  display: flex;
  align-items: center;
}

.new-session-div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  align-self: stretch;
  gap: 8px;
}

.custom-placeholder-color ::placeholder {
  overflow: hidden;
  color: var(--Primary);
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.24px;
  white-space: nowrap;
}

.hover-effect {
  margin-right: 4%;
  margin-bottom: 4%;
  cursor: pointer;
}

.custom-textarea-div .custom-textarea {
  height: 22vh;
  width: 15vw;
  background-color: var(--White);
}
</style>

<style>
.new-session-dropdown-div .p-dropdown {
  width: 75cqi;
  height: 35px;
}

.new-session-dropdown-div .p-dropdown-label {
  font-size: 12px;
}

.p-dropdown-item {
  font-size: 12px;
}

.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
  white-space: initial;
}

.new-session-notes-button-div .p-button-outlined {
  border: 1px solid var(--Grey400);
  width: 75cqi;
}

.new-session-notes-button-div .p-button-label {
  max-width: 235px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
}
</style>
